import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { api, useApi, type EpisodeRow, type PodcastRow, type StockRow } from '../api'
import { useIsMobile } from '../layout'
import { usePageTitle } from '../title'
import { fmtDate, fmtPct, fmtPrice, isStale } from '../format'
import { Empty, ErrorState, Icon, Loading, PodAv, Sparkline } from '../components/primitives'

// The design's MStocks / MPodcasts / MEpisodes. Desktop has no list design, so
// it shows the same rows in a column under a desktop page title.

function ListPage({ title, sub, children }: { title: string; sub: string; children: ReactNode }) {
  const mobile = useIsMobile()
  usePageTitle(title)
  return mobile ? (
    <div>
      <h1 className="m-h1">{title}</h1>
      <p className="m-sub">{sub}</p>
      {children}
    </div>
  ) : (
    <div className="list-col">
      <h1 className="page-title">{title}</h1>
      <p className="page-sub">{sub}</p>
      {children}
    </div>
  )
}

function useList<T>(path: string, render: (rows: T[]) => ReactNode, empty: string) {
  const { data, error, retry } = useApi<T[]>(path)
  if (error) return <ErrorState error={error} retry={retry} />
  if (!data) return <Loading />
  if (data.length === 0) return <Empty>{empty}</Empty>
  return render(data)
}

export function Stocks() {
  const body = useList<StockRow>(api.stocks(), (rows) => (
    <div className="m-list" data-testid="stock-list">
      {rows.map((s) => (
        <Link key={s.ticker} to={`/stocks/${s.ticker}`} className="m-row">
          <div className="meta">
            <span className="a tick">{s.ticker}</span>
            <span className="b">{s.name}</span>
          </div>
          <Sparkline values={s.closes} w={64} h={24} />
          <div className="col" style={{ alignItems: 'flex-end', minWidth: 72 }}>
            <span className="t-num" style={{ fontSize: 14 }}>{fmtPrice(s.last_close)}</span>
            {isStale(s.last_ts) && s.last_ts ? (
              <span className="t-sm" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>as of {fmtDate(s.last_ts)}</span>
            ) : (
              <span className={`t-num ${(s.day_change_pct ?? 0) >= 0 ? 'text-up' : 'text-down'}`} style={{ fontSize: 12 }}>
                {fmtPct(s.day_change_pct)}
              </span>
            )}
          </div>
        </Link>
      ))}
    </div>
  ), 'No stocks have been recommended yet.')
  return <ListPage title="Stocks" sub="Tickers mentioned across tracked podcasts.">{body}</ListPage>
}

export function Podcasts() {
  const body = useList<PodcastRow>(api.podcasts(), (rows) => (
    <div className="m-list" data-testid="podcast-list">
      {rows.map((p) => (
        <Link key={p.id} to={`/podcasts/${p.id}`} className="m-row">
          <PodAv name={p.name} image={p.image_url} size="lg sq" />
          <div className="meta">
            <span className="a">{p.name}</span>
            <span className="b">{p.pick_count === 1 ? '1 pick' : `${p.pick_count} picks`} · {p.episodes_analyzed} episodes</span>
          </div>
          <span className="chev"><Icon name="chevron" size={16} /></span>
        </Link>
      ))}
    </div>
  ), 'No podcasts are being tracked.')
  return <ListPage title="Podcasts" sub="Shows we extract recommendations from.">{body}</ListPage>
}

export function Episodes() {
  const body = useList<EpisodeRow>(api.episodes(), (rows) => (
    <div className="m-list" data-testid="episode-list">
      {rows.map((e) => (
        <Link key={e.id} to={`/episodes/${e.id}`} className="m-row">
          <PodAv name={e.podcast_name} image={e.podcast_image_url} size="lg sq" />
          <div className="meta">
            <span className="a">{e.title}</span>
            <span className="b">{e.podcast_name} · {fmtDate(e.pub_date)} · {e.tickers.join(', ')}</span>
          </div>
          <span className="chev"><Icon name="chevron" size={16} /></span>
        </Link>
      ))}
    </div>
  ), 'No episodes with picks yet.')
  return <ListPage title="Episodes" sub="Recent episodes with extracted picks.">{body}</ListPage>
}
